import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import LightbulbOnIcon from 'icons/LightbulbOn.icon';
import useQueryFilter from 'hooks/useQueryFilter';
import utils, {
  getArrayLength,
  getDataRoomRoute,
  getHasElementsInArray,
} from 'utils';
import DashCardHeading from 'router/subrouters/DataRoom/pages/Dashboard/componenets/DashCardHeading';
import SegmentedToggle from 'components/SegmentedToggle';
import useEnterpriseDealsQuery from './hooks/useEnterpriseDealsQuery';
import useEnterpriseSuggestedActionsQuery from './hooks/useEnterpriseSuggestedActionsQuery';
import useEnterpriseUndoActions from './hooks/useEnterpriseUndoActions';
import useGeneralUndoActions from './hooks/useGeneralUndoActions';
import useGeneralSuggestedActionsQuery from './hooks/useGeneralSuggestedActionsQuery';
import EnterpriseSuggestedActionsSection from './components/EnterpriseSuggestedActionsSection';
import EnterpriseSuggestedActionItem from './components/EnterpriseSuggestedActionItem';
import GeneralSuggestedActionItem from './components/GeneralSuggestedActionItem';
import EnterpriseSuggestedActionsPlaceholder from './components/EnterpriseSuggestedActionsPlaceholder';
import SuggestedActionTypeFilter from './components/SuggestedActionTypeFilter';
import SuggestedActionsDealFilter from './components/SuggestedActionsDealFilter';
import useSuggestedActionsMultiSort from './hooks/useSuggestedActionsMultiSort';
import MultiSortPanel from 'components/MultiSortPanel';
import DefaultSuggestedActionsEmptyState from 'components/DefaultSuggestedActionsEmptyState';
import ResolvePagination from 'components/ResolvePagination';
import ClearFilters from 'components/ClearFilters';
import useResetPage from 'router/subrouters/Dash/subrouters/hooks/useResetPage';
import { FIRST_PAGE } from 'data/global';
import useScrollIntoView from 'hooks/useScrollIntoView';
import {
  HAS_ACTIONS_FILTER,
  SUGGESTED_ACTIONS_ACTION_TYPE_KEY,
  SUGGESTED_ACTIONS_HAS_ACTIONS_KEY,
} from './suggestedActionsFilterData';
import { Link } from 'react-router-dom';

import './EnterpriseSuggestedActions.styles.scss';

export const SUGGESTED_ACTIONS_DEAL_FILTER_KEY = 'suggestedActionsDealRoomId';
export const SUGGESTED_ACTIONS_PAGE_KEY = 'suggestedActionsPage';

// Filters cleared by the "Clear Filters" button. The toggle is included since
// its default (`WITH_ACTIONS`) is the absence of the param.
const SUGGESTED_ACTIONS_FILTER_KEYS = [
  SUGGESTED_ACTIONS_ACTION_TYPE_KEY,
  SUGGESTED_ACTIONS_DEAL_FILTER_KEY,
];

// Height of the sticky `.resolve-header`, kept clear when scrolling to the top
// of the section on pagination.
const RESOLVE_HEADER_HEIGHT = 80;

type EnterpriseSuggestedActionsProps = {
  className?: string;
  enterpriseId: string;
};

const EnterpriseSuggestedActions: React.FC<EnterpriseSuggestedActionsProps> = (
  props,
) => {
  const { className, enterpriseId } = props;

  const { t } = useTranslation();

  const { data: deals } = useEnterpriseDealsQuery(enterpriseId);

  const { params, onFiltersApply, applyQueryFilter } = useQueryFilter();

  const page = params[SUGGESTED_ACTIONS_PAGE_KEY];

  const sort = params.sort;

  const hasActionsFilter =
    params[SUGGESTED_ACTIONS_HAS_ACTIONS_KEY] ||
    HAS_ACTIONS_FILTER.WITH_ACTIONS;

  const dealRoomIds = useMemo(() => {
    const selected = utils.mapParamsToArray(
      params[SUGGESTED_ACTIONS_DEAL_FILTER_KEY],
    );
    return getHasElementsInArray(selected) ? selected : undefined;
  }, [params]);

  const hasSuggestedActions = useMemo(() => {
    if (hasActionsFilter === HAS_ACTIONS_FILTER.WITH_ACTIONS) return true;
    if (hasActionsFilter === HAS_ACTIONS_FILTER.WITHOUT_ACTIONS) return false;
    return undefined;
  }, [hasActionsFilter]);

  const actionTypes = useMemo(() => {
    const selected = utils.mapParamsToArray(
      params[SUGGESTED_ACTIONS_ACTION_TYPE_KEY],
    );
    return getHasElementsInArray(selected) ? selected : undefined;
  }, [params]);

  const suggestedActionsFilters = useMemo(
    () => ({ hasSuggestedActions, actionTypes }),
    [hasSuggestedActions, actionTypes],
  );

  const { data, isLoading: isLoadingSuggestions } =
    useEnterpriseSuggestedActionsQuery(
      enterpriseId,
      dealRoomIds,
      page,
      suggestedActionsFilters,
      sort,
    );

  const { multiSortValues, multiSortDirectionTranslation } =
    useSuggestedActionsMultiSort();

  const handleHasActionsChange = useCallback(
    (value: string | number | boolean) => {
      onFiltersApply(
        SUGGESTED_ACTIONS_HAS_ACTIONS_KEY,
        value === HAS_ACTIONS_FILTER.WITH_ACTIONS ? '' : String(value),
      );
      applyQueryFilter(SUGGESTED_ACTIONS_PAGE_KEY, FIRST_PAGE);
    },
    [onFiltersApply, applyQueryFilter],
  );

  const hasActionsToggleOptions = useMemo(
    () => [
      { label: t('allDeals'), value: HAS_ACTIONS_FILTER.ALL },
      { label: t('withActions'), value: HAS_ACTIONS_FILTER.WITH_ACTIONS },
      { label: t('withoutActions'), value: HAS_ACTIONS_FILTER.WITHOUT_ACTIONS },
    ],
    [t],
  );

  const { data: generalActions } = useGeneralSuggestedActionsQuery();

  useResetPage(
    data?.totalPages || 0,
    page || FIRST_PAGE,
    SUGGESTED_ACTIONS_PAGE_KEY,
  );

  const dealOptions = useMemo(
    () =>
      (deals || []).map(({ _id, name }) => ({
        value: _id,
        label: name,
      })),
    [deals],
  );

  const isFirstPage = !page || page === FIRST_PAGE;

  // The personal suggestions are user-level (not paginated), so they are only
  // shown before the first page of Data Room suggestions.
  const visibleGeneralActions = useMemo(
    () => (isFirstPage ? generalActions || [] : []),
    [generalActions, isFirstPage],
  );

  const visibleDataRooms = useMemo(() => data?.items || [], [data?.items]);

  const hasPersonalSuggestions = getHasElementsInArray(visibleGeneralActions);

  const hasVisibleSuggestions =
    getHasElementsInArray(visibleDataRooms) || hasPersonalSuggestions;

  // Offset the scroll target by the sticky header so the section heading lands
  // just below the nav rather than underneath it.
  const [containerRef, scrollToStart] = useScrollIntoView(
    RESOLVE_HEADER_HEIGHT,
  );

  const resetPage = useCallback(() => {
    if (!isFirstPage) {
      applyQueryFilter(SUGGESTED_ACTIONS_PAGE_KEY, FIRST_PAGE);
      scrollToStart();
    }
  }, [applyQueryFilter, scrollToStart, isFirstPage]);

  const { undoNotifications, ...undoHandlers } =
    useEnterpriseUndoActions(resetPage);

  const {
    undoNotifications: generalUndoNotifications,
    ...generalUndoHandlers
  } = useGeneralUndoActions();

  const classes = classNames(
    'enterprise-suggested-actions',
    'white-panel',
    className,
  );

  return (
    <div ref={containerRef} className={classes}>
      <DashCardHeading
        heading={t('suggestedActionsLc')}
        icon={LightbulbOnIcon}
        showCounter={false}
        showTimeframe={false}
      />

      <div className="enterprise-suggested-actions__filters">
        <SegmentedToggle
          className="enterprise-suggested-actions__filters__toggle"
          options={hasActionsToggleOptions}
          selectedValue={hasActionsFilter}
          onChange={handleHasActionsChange}
        />

        <div className="enterprise-suggested-actions__filters__actions">
          <ClearFilters
            filters={SUGGESTED_ACTIONS_FILTER_KEYS}
            pageKey={SUGGESTED_ACTIONS_PAGE_KEY}
          />

          <SuggestedActionTypeFilter pageKey={SUGGESTED_ACTIONS_PAGE_KEY} />

          <SuggestedActionsDealFilter
            filterKey={SUGGESTED_ACTIONS_DEAL_FILTER_KEY}
            pageKey={SUGGESTED_ACTIONS_PAGE_KEY}
            options={dealOptions}
          />

          <MultiSortPanel
            className="enterprise-suggested-actions__filters__actions__sorts"
            options={multiSortValues}
            sortDirectionTranslations={multiSortDirectionTranslation}
          />
        </div>
      </div>

      {isLoadingSuggestions ? (
        <EnterpriseSuggestedActionsPlaceholder />
      ) : !hasVisibleSuggestions ? (
        <DefaultSuggestedActionsEmptyState />
      ) : (
        <div className="enterprise-suggested-actions__wrapper">
          {hasPersonalSuggestions && (
            <EnterpriseSuggestedActionsSection
              title={t('personal')}
              count={getArrayLength(visibleGeneralActions)}
            >
              {visibleGeneralActions.map((suggestion) => (
                <GeneralSuggestedActionItem
                  key={suggestion.type}
                  suggestion={suggestion}
                  {...generalUndoHandlers}
                />
              ))}
            </EnterpriseSuggestedActionsSection>
          )}
          {visibleDataRooms.map(
            ({ dataRoomId, dealRoomId, dealRoomName, actions }) => (
              <EnterpriseSuggestedActionsSection
                key={dataRoomId}
                title={dealRoomName}
                count={getArrayLength(actions)}
              >
                {getHasElementsInArray(actions) ? (
                  actions.map((suggestion) => (
                    <EnterpriseSuggestedActionItem
                      key={suggestion.type}
                      dataRoomId={dataRoomId}
                      dealRoomId={dealRoomId}
                      suggestion={suggestion}
                      {...undoHandlers}
                    />
                  ))
                ) : (
                  <DefaultSuggestedActionsEmptyState
                    className="no-actions-for-data-room"
                    title={t('youAreUpToDate')}
                  >
                    <p>{t('noSuggestedActionsForDataRoom')}</p>
                    <Link
                      className="ncoded-button ncoded-button--outline"
                      to={getDataRoomRoute(dataRoomId)}
                    >
                      {t('goToDataRoom')}
                    </Link>
                  </DefaultSuggestedActionsEmptyState>
                )}
              </EnterpriseSuggestedActionsSection>
            ),
          )}
        </div>
      )}

      <ResolvePagination
        totalPages={data?.totalPages || 0}
        pageParam={SUGGESTED_ACTIONS_PAGE_KEY}
        onClick={scrollToStart}
      />

      {undoNotifications}
      {generalUndoNotifications}
    </div>
  );
};

export default EnterpriseSuggestedActions;
